import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { ref, onValue } from 'firebase/database';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaFilePdf } from 'react-icons/fa';
import EngineerLayout from './EngineerLayout';

const EngineerWorkDetails = () => {
    const { category, id } = useParams();
    const navigate = useNavigate();
    const [work, setWork] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const workPath = category === 'General' ? `ekamwatapzp/WorkNames/${id}` : `ekamwatapzp/WorkNames/${category}/${id}`;
        const workRef = ref(db, workPath);
        const unsubscribe = onValue(workRef, (snapshot) => {
            if (snapshot.exists()) {
                setWork(snapshot.val());
            } else {
                setWork(null);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [category, id]);

    if (loading) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex items-center justify-center bg-gray-50">
                    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1a1a4d]"></div>
                </div>
            </EngineerLayout>
        );
    }

    if (!work) {
        return (
            <EngineerLayout>
                <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
                    <h2 className="text-xl font-bold text-gray-800 mb-4">माहिती सापडली नाही!</h2>
                    <button 
                        onClick={() => navigate(-1)}
                        className="flex items-center gap-2 px-6 py-2 bg-[#1a1a4d] text-white rounded-lg hover:bg-opacity-90 transition"
                    >
                        <FaArrowLeft /> मागे जा
                    </button>
                </div>
            </EngineerLayout>
        );
    }

    // Helper to render a pair of (Label: Value) in a row
    const DetailRow = ({ label1, value1, label2, value2 }) => (
        <tr className="border-b border-gray-200">
            <td className="w-[15%] px-4 py-3 bg-[#f8f9fa] text-[13px] font-bold text-gray-700 border-r border-gray-200">{label1}</td>
            <td className="w-[35%] px-4 py-3 text-[13px] text-gray-600 font-medium border-r border-gray-200">{value1 || '-'}</td>
            <td className="w-[15%] px-4 py-3 bg-[#f8f9fa] text-[13px] font-bold text-gray-700 border-r border-gray-200">{label2}</td>
            <td className="w-[35%] px-4 py-3 text-[13px] text-gray-600 font-medium">{value2 || ''}</td>
        </tr>
    );

    // Helper for PDF link rows (2 column layout)
    const PdfRow = ({ label1, url1, label2, url2 }) => (
        <tr className="border-b border-gray-200">
            <td className="w-[15%] px-4 py-3 bg-[#f8f9fa] text-[13px] font-bold text-gray-700 border-r border-gray-200">{label1}</td>
            <td className="w-[35%] px-4 py-3 border-r border-gray-200">
                {url1 ? (
                    <div 
                        className="p-1 border border-gray-200 rounded w-fit cursor-pointer hover:bg-gray-100 transition"
                        onClick={() => window.open(url1, '_blank')}
                    >
                        <FaFilePdf className="text-red-500 text-lg" />
                    </div>
                ) : '-'}
            </td>
            <td className="w-[15%] px-4 py-3 bg-[#f8f9fa] text-[13px] font-bold text-gray-700 border-r border-gray-200">{label2}</td>
            <td className="w-[35%] px-4 py-3">
                {url2 ? (
                    <div 
                        className="p-1 border border-gray-200 rounded w-fit cursor-pointer hover:bg-gray-100 transition"
                        onClick={() => window.open(url2, '_blank')}
                    >
                        <FaFilePdf className="text-red-500 text-lg" />
                    </div>
                ) : '-'}
            </td>
        </tr>
    );

    return (
        <EngineerLayout>
            <div className="p-4 md:p-8 bg-[#f4f7fe] min-h-screen font-sans">
                {/* Header / Breadcrumb Section */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 max-w-[1300px] mx-auto gap-4">
                    <div className="flex items-center gap-4">
                        <button 
                            onClick={() => navigate(-1)}
                            className="p-2.5 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-400 hover:text-[#1a1a4d] transition active:scale-95"
                        >
                            <FaArrowLeft />
                        </button>
                        <h1 className="text-2xl font-bold text-gray-800">अर्जदाराची कागदपत्रे (Work Documents)</h1>
                    </div>
                    <div className="flex items-center gap-2 text-[12px] font-bold text-gray-400">
                        <span className="cursor-pointer hover:text-gray-600 transition">सांगली जिल्हा परिषद</span>
                        <span>/</span>
                        <span className="text-blue-500 cursor-default">कामाची कागदपत्रे</span>
                    </div>
                </div>

                {/* Main Content Card */}
                <div className="max-w-[1300px] mx-auto bg-white rounded-3xl shadow-xl shadow-blue-100/20 border border-gray-100 overflow-hidden">
                    <div className="p-8 md:p-12">
                        {/* Work Title Banner */}
                        <div className="mb-10 pb-4 border-b border-gray-100">
                            <h2 className="text-[20px] font-bold text-[#42a5f5] leading-relaxed">
                                कामाचे नाव - {work.workName}
                            </h2>
                        </div>

                        {/* Information Table */}
                        <div className="border border-gray-200 rounded-2xl overflow-hidden">
                            <table className="w-full text-left border-collapse">
                                <tbody>
                                    <DetailRow 
                                        label1="तालुका" value1={work.taluka} 
                                        label2="योजनेचे नाव" value2={work.schemeName}
                                    />
                                    <DetailRow 
                                        label1="निविदा रक्कम" value1={work.tenderAmount} 
                                        label2="जावक नंबर / प्रमा नंबर / दिनांक" value2={work.inwardNo}
                                    />
                                    <DetailRow 
                                        label1="दिनांक" value1={work.date} 
                                        label2="प्रशासकीय मंजुरीचा अधिकार" value2={work.adminApprovalAuthority}
                                    />
                                    <DetailRow 
                                        label1="जावक.क्र दिनांक" value1={work.inwardDate} 
                                        label2="तांत्रिक मंजूर आदेश क्र." value2={work.technicalApprovalOrderNo}
                                    />
                                    <DetailRow 
                                        label1="अंदाजपत्रकीय तरतूद सन" value1={work.budgetaryProvisionYear} 
                                        label2="काम पुर्ण करणेची मुदत" value2={work.completionPeriod}
                                    />
                                    <DetailRow 
                                        label1="टेंडरचा प्रकार" value1={work.tenderType} 
                                        label2="वर्क नंबर" value2={work.workNumber}
                                    />
                                    <DetailRow 
                                        label1="Work Portion" value1={work.recapitulation?.workPortion} 
                                        label2="Insurance" value2={work.recapitulation?.insurance}
                                    />
                                    <DetailRow 
                                        label1="GST" value1={work.recapitulation?.gst} 
                                        label2="Other" value2={work.recapitulation?.other}
                                    />
                                    <DetailRow 
                                        label1="Total Amount" value1={work.recapitulation?.total} 
                                        label2="कामाचा प्रकार" value2={work.workType}
                                    />
                                    {/* Documents Section */}
                                    <PdfRow 
                                        label1="AA Upload" url1={work.aaUploadUrl} 
                                        label2="Recap Upload" url2={work.recapUploadUrl}
                                    />
                                    <PdfRow 
                                        label1="TS Upload" url1={work.tsUploadUrl} 
                                        label2="DTP Upload" url2={work.dtpUploadUrl}
                                    />
                                    <PdfRow 
                                        label1="Estimate Upload" url1={work.estimateUploadUrl} 
                                        label2="" url2=""
                                    />
                                </tbody>
                            </table>
                        </div>

                        {/* Thick Bottom Line */}
                        <div className="mt-12 border-t-[4px] border-[#1a1a4d]"></div>
                    </div>
                </div>
            </div>
        </EngineerLayout>
    );
};

export default EngineerWorkDetails;
